<template>
    <div class="ls-flow--container">
        <LsTemplatedLoading :show="loading" :full-screen="true" test-id="loading" />
        <div v-if="!loading" test-id="form_wrapper">
            <div class="ls-flow--title">Confirm Skip</div>

            <form novalidate @submit.prevent="createScheduleSkip">
                <div>
                    <p>Skip service scheduled on</p>
                    <p class="ls-flow--date-range">{{ skipDateRange }}</p>
                </div>

                <div>
                    <p>Next service scheduled on</p>
                    <p class="ls-flow--date-range">{{ nextDateRange }}</p>
                </div>

                <LsRadios
                    v-model="responsible_party"
                    track-id="skip_view-options"
                    name="responsible-party"
                    label="Responsible party"
                    :options="responsiblePartyOptions"
                    @input="updateResponsibleParty"
                />

                <LsSelect
                    v-if="responsible_party !== 'lawnstarter'"
                    v-model="reason"
                    track-id="skip_view-reason_options"
                    name="reason"
                    :options="
                        responsible_party === 'customer'
                            ? customerReasonOptions
                            : providerReasonOptions
                    "
                    label="Reason for skipping"
                    class="ls-flow--330"
                    :validation="{ required: true }"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="skip_view-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: true, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="skip_view-confirm_skip"
                    text="Confirm Skip"
                    :loading="isUpdating"
                    type="submit"
                />
            </form>
        </div>
    </div>
</template>

<script>
import _ from 'lodash';
import { lsErrorService, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';
import { lsHelpers } from '@/helpers';
import ScheduleSkip from '@/graphql/queries/ScheduleSkip.gql';
import CreateScheduleSkip from '@/graphql/mutations/CreateScheduleSkip.gql';
import { LsButton, LsRadios, LsInput, LsSelect } from '@/components/common';
import { FlowSuccessModal } from '@/modals';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { apolloClient } from '@/vue-apollo';
import LsError from '@/components/common/LsError/LsError.vue';

const responsiblePartyOptions = [
    {
        value: 'customer',
        label: 'Customer',
    },
    {
        value: 'provider',
        label: 'Provider',
    },
    {
        value: 'lawnstarter',
        label: 'LawnStarter',
    },
];

const customerReasonOptions = [
    {
        value: 'already_mowed',
        label: 'Already mowed',
    },
    {
        value: 'bad_weather',
        label: 'Bad weather',
    },
    {
        value: 'customer_did_not_provide_reason',
        label: 'Customer did not provide reason',
    },
    {
        value: 'conflicting_event',
        label: 'Conflicting event',
    },
    {
        value: 'customer_unavailable',
        label: 'Customer unavailable',
    },
    {
        value: 'does_not_have_money',
        label: `Does not have the money`,
    },
    {
        value: 'grass_too_short',
        label: 'Grass too short',
    },
    {
        value: 'lawn_too_wet',
        label: 'Lawn is too wet',
    },
    {
        value: 'lawn_just_treated',
        label: 'Lawn was just treated, aerated, etc.',
    },
    {
        value: 'obstruction_on_property',
        label: 'Obstruction on the property',
    },
    {
        value: 'skipped_instead_of_pausing',
        label: 'Skipped instead of pausing service',
    },
    {
        value: 'waiting_on_property_damage_resolution',
        label: 'Waiting on their property damage resolution',
    },
    {
        value: 'want_to_be_home',
        label: 'Want to be home to watch crew',
    },
];

const providerReasonOptions = [
    {
        value: 'already_mowed',
        label: 'Already mowed',
    },
    {
        value: 'bad_weather',
        label: 'Bad weather',
    },
    {
        value: 'cannot_locate_property',
        label: 'Cannot locate property',
    },
    {
        value: 'customer_asked_not_to_mow',
        label: 'Customer asked not to mow',
    },
    {
        value: 'grass_too_short',
        label: 'Grass too short',
    },
    {
        value: 'lawn_is_too_wet',
        label: 'Lawn is too wet',
    },
    {
        value: 'obstruction_on_the_property',
        label: 'Obstruction on the property',
    },
    {
        value: 'provider_unavailable',
        label: 'Provider unavailable',
    },
];

export default {
    name: 'SkipView',
    components: {
        LsButton,
        LsRadios,
        LsInput,
        LsTemplatedLoading,
        LsError,
        LsSelect,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,
            responsiblePartyOptions,
            customerReasonOptions,
            providerReasonOptions,

            property: apolloClient.readFragment({
                id: `Property:${this.$route.params.property_id}`,
                fragment: gql`
                    fragment property on Property {
                        street
                    }
                `,
            }),

            schedule: apolloClient.readFragment({
                id: `Schedule:${this.$route.params.schedule_id}`,
                fragment: gql`
                    fragment schedule on Schedule {
                        next_customer_starts_at
                        next_customer_ends_at
                    }
                `,
            }),

            // form data
            responsible_party: 'customer',
            reason: null,
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        scheduleSkip: {
            query: ScheduleSkip,
            variables() {
                return {
                    customer_id: _.toNumber(this.$route.params.customer_id),
                    property_id: _.toNumber(this.$route.params.property_id),
                    schedule_id: _.toNumber(this.$route.params.schedule_id),
                };
            },
            result({ data, loading }) {
                if (!loading) {
                    const {
                        next_customer_starts_at,
                        next_customer_ends_at,
                        skip_date_starts_at,
                        skip_date_ends_at,
                    } = data.scheduleSkip;

                    this.nextDateRange = lsHelpers.dateRange({
                        start: next_customer_starts_at,
                        end: next_customer_ends_at,
                    });

                    this.skipDateRange = lsHelpers.dateRange({
                        start: skip_date_starts_at,
                        end: skip_date_ends_at,
                    });
                }
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleSkip.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            loadingKey: 'loading',
            // "no-cache" would be better here - but the loading state was being wonky when I tried that
            fetchPolicy: 'network-only',
        },
    },
    methods: {
        updateResponsibleParty() {
            this.reason = null;
        },
        async createScheduleSkip() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateScheduleSkip,
                    variables: {
                        customer_id: _.toNumber(this.$route.params.customer_id),
                        property_id: _.toNumber(this.$route.params.property_id),
                        schedule_id: _.toNumber(this.$route.params.schedule_id),
                        responsible_party: this.responsible_party,
                        reason: this.reason === null ? 'lawnstarter' : this.reason,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service skipped for ${this.property.street}`,
                            copy: data.createScheduleSkip.copy,
                            displayServicesOnDate: {
                                next_customer_starts_at: this.scheduleSkip.skip_date_starts_at,
                                next_customer_ends_at: this.scheduleSkip.skip_date_ends_at,
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },
    },
};
</script>
